import log from '@/lib/logger';
import type { AppDispatch, RootState } from '@/store';
import type { NormalizedVehicleResponse } from '@/types/client';
import type { Vehicle } from '@/types/swagger';
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import { vehicleListSchema } from 'features/schemas';
import { loadLastTrackerLog } from 'features/TrackerLogs';
import _ from 'lodash';
import { normalize } from 'normalizr';
import Nprogress from 'nprogress';
import { callNextPage } from 'util/callNextPage';
import { getNewImmutableState } from 'util/getNewImmutableState';
import { getHumanizedAddress } from 'util/mapboxService';

// ----------------------------------- Actions ---------------------------------
// Action Types
const LOAD_VEHICLES = 'vehicles/load_vehicles';
const SELECT_VEHICLE = 'vehicles/select_vehicle';
const UNSELECT_VEHICLES = 'vehicles/unselect_vehicles';
const LOAD_VEHICLE_STATUSES = 'vehicle/load_vehicle_statuses';
const FETCH_START = 'vehicle/fetch_start';
const FETCH_END = 'vehicle/fetch_end';

// Set vehicles to state
export const loadVehicles = (vehicles: Record<number, Vehicle>) => ({
  type: LOAD_VEHICLES,
  payload: vehicles,
});
export const selectVehicle = (vehicleId: number) => ({
  type: SELECT_VEHICLE,
  payload: vehicleId,
});
export const unselectVehicles = () => ({ type: UNSELECT_VEHICLES });
export const loadVehicleStatuses = (number_plate: string, vehicleStatuses: unknown) => ({
  type: LOAD_VEHICLE_STATUSES,
  meta: { number_plate },
  payload: vehicleStatuses,
});
export const fetchStart = () => ({ type: FETCH_START });
export const fetchEnd = () => ({ type: FETCH_END });

// ----------------------------------- Reducer ---------------------------------
interface VehiclesState {
  byId: Record<number, any>;
  allIds: number[];
  selectedVehicleId: number | null;
  trackerLogsByNumberPlate: Record<string, any>;
  fetching: boolean;
}

// Init State
const INIT_STATE: VehiclesState = {
  byId: {},
  allIds: [],
  selectedVehicleId: null,
  trackerLogsByNumberPlate: {},
  fetching: false,
};

// Reducer
const vehicleReducer = (state = INIT_STATE, action: UnknownAction): VehiclesState => {
  const { type, meta, payload } = action as PayloadAction<unknown> & {
    meta: { number_plate: string };
  };
  switch (type) {
    case LOAD_VEHICLES: {
      return getNewImmutableState(state, payload);
    }
    case SELECT_VEHICLE: {
      return { ...state, selectedVehicleId: payload as number };
    }
    case UNSELECT_VEHICLES: {
      return { ...state, selectedVehicleId: null };
    }
    case LOAD_VEHICLE_STATUSES: {
      return getNewImmutableState(
        state,
        { [meta.number_plate]: payload },
        'vehicleStatusesByNumberPlate',
        true,
        true
      );
    }
    case FETCH_START: {
      return { ...state, fetching: true };
    }
    case FETCH_END: {
      return { ...state, fetching: false };
    }
    default: {
      return state;
    }
  }
};

export default vehicleReducer;

// ----------------------------------- Side Effects ----------------------------
// Thunks
export const fetchVehicles = () => async (dispatch: AppDispatch) => {
  Nprogress.start();
  const apiVehicles = 'telemetry/vehicle/?page_size=100';
  dispatch(fetchStart());
  const resolve = (data: unknown) => {
    const normalizedData: NormalizedVehicleResponse = normalize(data, vehicleListSchema);
    const { vehicles } = normalizedData.entities;
    if (vehicles) dispatch(loadVehicles(vehicles));
    _.forEach(vehicles, (vehicle) => {
      if (vehicle.latest_tracker_log) {
        getHumanizedAddress(vehicle.latest_tracker_log).then((placeName) => {
          const lastTrackerLog = vehicle.latest_tracker_log;
          if (lastTrackerLog) {
            // lastTrackerLog.place_name = placeName;
            dispatch(loadLastTrackerLog(vehicle.number_plate, lastTrackerLog));
          }
        });
      }
    });
    dispatch(fetchEnd());
    Nprogress.done();
    return data;
  };
  const reject = (error: AxiosError) => {
    log.error(error);
    dispatch(fetchEnd());
    Nprogress.done();
    return error;
  };
  return callNextPage(apiVehicles, resolve, reject);
};

export const getVehiclesSelector = (state: RootState) => state.vehicles.byId;
export const getSelectedVehicleIdSelector = (state: RootState) => state.vehicles.selectedVehicleId;
